import React, { useState, useEffect, FC, useCallback } from 'react';
import Modal from 'react-modal';
import useEEActive from '../../../hooks/useEEActive';
import { ModalProps } from '../../../types/types';
import {
  adjustModalPositionBasedOnContent,
  isElementHeightValid,
} from '../../../utils/modal.utils';
import { documentService, windowService } from '../../../utils/window.utils';
import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import CloseButton from './components/closeButton';
import useMediaQuery from '../../../lib/custom-hooks/useMediaQuery';
import { BreakpointsInPx } from '../../../constants/breakpoints.constants';

const ModalDialog: FC<ModalProps> = ({
  isOpen = false,
  close,
  children,
  shouldCloseOnOverlayClick = true,
  className,
  overlayClassName = className,
  hasMaxHeightRestricted = false,
  onAfterOpen = undefined,
  hideCloseButton = false,
  skipModalPositionAdjustment = false,
}: ModalProps) => {
  const [contentEleNode, setContentEleNode] = useState<HTMLDivElement | null>(null);
  const [alignClassName, setAlignClassName] = useState<string>('');
  const isEEActiveOrPreview = useEEActive(true);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['md']})`);

  const adjustToToolbarHeight = (element: HTMLIFrameElement): void => {
    const height = element.clientHeight;
    if (!height) return;
    documentService().documentElement?.style?.setProperty('--ee-toolbar-offset', `${height}px`);
  };

  useEffect(() => {
    const resize = (): void => {
      adjustModalPositionBasedOnContent();
    };
    windowService().addEventListener('resize', resize);
    return (): void => {
      windowService().removeEventListener('resize', resize);
    };
  }, []);

  useEffect(() => {
    if (isOpen && isEEActiveOrPreview && document) {
      const EEToolbar = documentService().getElementById('scWebEditRibbon') as HTMLIFrameElement;
      if (!EEToolbar) return;

      if (!('MutationObserver' in window)) return;

      adjustToToolbarHeight(EEToolbar);

      const mutationCallback = (mutationsList: MutationRecord[]): void => {
        for (const mutation of mutationsList) {
          if ((mutation?.target as HTMLIFrameElement)?.id !== 'scWebEditRibbon') return;
          adjustToToolbarHeight(mutation?.target as HTMLIFrameElement);
        }
      };

      const observer = new MutationObserver(mutationCallback);

      const config = { attributes: true, childList: false, subtree: false };

      observer.observe(EEToolbar, config);

      return (): void => {
        observer.disconnect();
      };
    }
  }, [isOpen, isEEActiveOrPreview]); // eslint-disable-line

  useEffect(() => {
    if (!contentEleNode || !isOpen || skipModalPositionAdjustment) return;
    if (!('MutationObserver' in window)) return;
    let modalHeight = contentEleNode?.getBoundingClientRect()?.height || 0;
    const observer = new MutationObserver(() => {
      const newModalHeight = contentEleNode?.getBoundingClientRect()?.height;
      if (newModalHeight !== modalHeight) {
        modalHeight = newModalHeight;
        adjustModalPositionBasedOnContent();
      }
    });
    const config = { attributes: false, childList: true, subtree: true };
    observer.observe(contentEleNode, config);
    return (): void => {
      observer?.disconnect();
    };
  }, [contentEleNode, isOpen, skipModalPositionAdjustment]);

  const closeModal = (): void => {
    close && close(false);
  };

  const handleModalPosition = () => {
    if (isServer()) return;

    if (!contentEleNode) return;

    if (isElementHeightValid(contentEleNode)) {
      setAlignClassName('top-aligned-modal');
    } else {
      setAlignClassName('center-aligned-modal');
    }
  };

  const onAfterOpenCallback = useCallback((): void => {
    !skipModalPositionAdjustment && handleModalPosition();
    if (onAfterOpen) {
      onAfterOpen();
    }
  }, [contentEleNode, onAfterOpen]);

  const overlayElementCallback = useCallback(
    (
      props: JSX.IntrinsicAttributes &
        React.ClassAttributes<HTMLDivElement> &
        React.HTMLAttributes<HTMLDivElement>,
      contentElement: JSX.Element
    ): JSX.Element => {
      return (
        <div {...props}>
          {!hideCloseButton && !isDesktop && (
            <CloseButton closeModal={closeModal} isEEActiveOrPreview={isEEActiveOrPreview} />
          )}
          <>{contentElement}</>
        </div>
      );
    },
    [closeModal, isEEActiveOrPreview, isDesktop, hideCloseButton]
  );

  return (
    <Modal
      isOpen={isOpen}
      onAfterOpen={onAfterOpenCallback}
      parentSelector={(): void => documentService().querySelector('#root')}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      className={`modal-dialog ${alignClassName} ${className}
      ${isEEActiveOrPreview ? 'modal-dialog__overlay--ee-active' : ''}
      ${hasMaxHeightRestricted ? 'modal-dialog--has-max-height' : ''}`}
      overlayClassName={`modal-dialog__overlay ${overlayClassName} ${
        hasMaxHeightRestricted ? 'modal-dialog__overlay--has-max-height' : ''
      }`}
      overlayElement={overlayElementCallback}
      appElement={
        typeof document !== 'undefined' ? documentService()?.querySelector('#root') : null
      }
      contentRef={setContentEleNode}
    >
      {!hideCloseButton && isDesktop && (
        <CloseButton closeModal={closeModal} isEEActiveOrPreview={isEEActiveOrPreview} />
      )}
      {children}
    </Modal>
  );
};
export default ModalDialog;
